import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'

import config from '~/src/App/config'

const LOGROCKET_CHILD_DOMAINS = ['https://addium.marketing']
const LOGROCKET_RELEASE = config.VERSION ? `v${config.VERSION}` : undefined
const REQUEST_BODY_KEY_PATTERNS = [
  /password/i,
  /^refresh$/i,
  /^refresh_?token$/i,
  /^token$/i,
]
const RESPONSE_BODY_KEY_PATTERNS = [
  /^access$/i,
  /^access_?token$/i,
  /^refresh$/i,
  /^refresh_?token$/i,
  /^token$/i,
]
const REDUX_KEY_PATTERNS = [
  /password/i,
  /token/i,
  /^access$/i,
  /^refresh$/i,
]
const REDACTED_VALUE = '[REDACTED]'

let initialized = false
let reactPluginInitialized = false
let lastIdentity = ''

const compact = values => Object.entries(values).reduce((acc, [key, value]) => {
  if (value !== null && typeof value !== 'undefined') {
    acc[key] = value
  }
  return acc
}, {})

const getLogRocketAppId = () => config.LOGROCKET_ID
const stringOrUndefined = value => (value !== null && typeof value !== 'undefined' ? String(value) : undefined)
const shouldRedactKey = (key, patterns) => patterns.some(pattern => pattern.test(key))

const omitSensitiveHeaders = (headers = {}) => (
  Object.entries(headers).reduce((acc, [key, value]) => {
    if (key.toLowerCase() !== 'authorization') {
      acc[key] = value
    }
    return acc
  }, {})
)

const getHeaderValue = (headers, name) => {
  const normalizedName = name.toLowerCase()
  const match = Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === normalizedName)
  return match?.[1] ?? ''
}

const isJsonContentType = contentType => (
  contentType.includes('application/json')
  || contentType.includes('+json')
)

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]'

const redactSensitiveFields = (value, patterns) => {
  if (Array.isArray(value)) {
    return value.map(item => redactSensitiveFields(item, patterns))
  }

  if (!value || !isPlainObject(value)) {
    return value
  }

  return Object.entries(value).reduce((acc, [key, entry]) => {
    acc[key] = shouldRedactKey(key, patterns) ? REDACTED_VALUE : redactSensitiveFields(entry, patterns)
    return acc
  }, {})
}

const sanitizeJsonBody = (body, patterns) => {
  const parsed = JSON.parse(body)
  return JSON.stringify(redactSensitiveFields(parsed, patterns))
}

const sanitizeUrlEncodedBody = (body, patterns) => {
  const params = new URLSearchParams(body)
  params.forEach((_, key) => {
    if (shouldRedactKey(key, patterns)) {
      params.set(key, REDACTED_VALUE)
    }
  })
  return params.toString()
}

const sanitizeBody = (body, patterns, contentType = '') => {
  if (!body) return body

  if (typeof body === 'string') {
    const normalizedContentType = contentType.toLowerCase()
    if (isJsonContentType(normalizedContentType)) {
      try {
        return sanitizeJsonBody(body, patterns)
      } catch (_) {
        return body
      }
    }
    if (normalizedContentType.includes('application/x-www-form-urlencoded')) {
      return sanitizeUrlEncodedBody(body, patterns)
    }
    if (normalizedContentType) {
      return body
    }

    try {
      return sanitizeJsonBody(body, patterns)
    } catch (_) {
      if (body.includes('=')) {
        return sanitizeUrlEncodedBody(body, patterns)
      }
      return body
    }
  }

  return redactSensitiveFields(body, patterns)
}

export const sanitizeLogRocketRequest = request => ({
  ...request,
  headers: omitSensitiveHeaders(request.headers),
  body: sanitizeBody(
    request.body,
    REQUEST_BODY_KEY_PATTERNS,
    getHeaderValue(request.headers, 'content-type')
  ),
})

export const sanitizeLogRocketResponse = response => ({
  ...response,
  body: sanitizeBody(
    response.body,
    RESPONSE_BODY_KEY_PATTERNS,
    getHeaderValue(response.headers, 'content-type')
  ),
})

export const sanitizeLogRocketReduxState = state => redactSensitiveFields(state, REDUX_KEY_PATTERNS)

export const sanitizeLogRocketReduxAction = action => redactSensitiveFields(action, REDUX_KEY_PATTERNS)

export const isLogRocketEnabled = () => (
  process.env.NODE_ENV !== 'test'
  && config.MSW !== 'ON'
  && Boolean(getLogRocketAppId())
)

export const initLogRocket = logger => {
  if (!isLogRocketEnabled()) return false
  if (initialized) return true

  try {
    logger.debug('starting LogRocket instrumentation')
    LogRocket.init(getLogRocketAppId(), compact({
      release: LOGROCKET_RELEASE,
      mergeIframes: true,
      childDomains: LOGROCKET_CHILD_DOMAINS,
      console: {
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: sanitizeLogRocketRequest,
        responseSanitizer: sanitizeLogRocketResponse,
      }
    }))
    initialized = true
  } catch (error) {
    logger.warn('unable to start LogRocket instrumentation', error)
    return false
  }

  if (!reactPluginInitialized && typeof document !== 'undefined' && document.body) {
    try {
      setupLogRocketReact()
      reactPluginInitialized = true
    } catch (error) {
      logger.warn('unable to start LogRocket React plugin', error)
    }
  }

  return true
}

export const getLogRocketUserProperties = ({
  account = {},
  clientVersion,
  visitor = {}
} = {}) => compact({
  name: visitor.name,
  email: visitor.email,
  admin: visitor.admin,
  dateJoined: visitor.dateJoined,
  facilityId: stringOrUndefined(account.id),
  facilityName: account.name,
  facilityApp: account.app,
  facilityState: account.state,
  facilityStatus: account.status,
  facilitySupportCategory: account.supportCategory,
  facilityTier: account.tier,
  facilityLocation: account.location,
  hasAutomait: account.hasAutomait,
  isApContact: visitor.isApContact,
  language: visitor.language,
  role: visitor.role,
  clientVersion,
})

export const identifyLogRocketUser = ({ account = {}, visitor = {}, ...pendo } = {}) => {
  if (!initialized || visitor.id === null || typeof visitor.id === 'undefined') return
  const identity = [visitor.id, account.id].filter(value => value !== null && typeof value !== 'undefined').join(':')
  if (identity === lastIdentity) return
  lastIdentity = identity

  LogRocket.identify(
    String(visitor.id),
    getLogRocketUserProperties({ account, visitor, ...pendo })
  )
}

export const captureLogRocketException = (error, options) => {
  if (!initialized || !error) return

  LogRocket.captureException(error, options)
}

export const captureLogRocketMessage = (message, options) => {
  if (!initialized || !message) return

  LogRocket.captureMessage(message, options)
}

export const getLogRocketReduxMiddleware = () => (
  isLogRocketEnabled()
    ? LogRocket.reduxMiddleware({
      stateSanitizer: sanitizeLogRocketReduxState,
      actionSanitizer: sanitizeLogRocketReduxAction,
    })
    : () => next => action => next(action)
)

export default LogRocket
